import { useState } from 'react';
import { useGetFormQuery } from '../../store/api/localApi';
import { FormFieldType, type FormResponse } from '../../types';

interface Props {
  response: FormResponse;
}

/**
 * Read-only accordion view of a submitted registration form. Loads the form
 * definition by `response.formId` so labels/types come from the form, not
 * the raw response keys.
 */
export function RegistrationSnapshot({ response }: Props) {
  const { data: formData, isLoading } = useGetFormQuery(response.formId);
  const [openSectionIds, setOpenSectionIds] = useState<Set<string>>(new Set());

  if (isLoading || !formData?.form) {
    return (
      <div data-testid="registration-snapshot-loading" className="p-4 text-sm text-gray-500">
        Loading registration details…
      </div>
    );
  }

  const form = formData.form;

  function toggleSection(id: string) {
    setOpenSectionIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div data-testid="registration-snapshot" className="divide-y divide-gray-100">
      {form.sections?.map((section) => {
        const isOpen = openSectionIds.has(section.id);
        return (
          <div key={section.id} data-testid={`registration-snapshot-section-${section.id}`}>
            <button
              type="button"
              onClick={() => toggleSection(section.id)}
              data-testid={`registration-snapshot-section-${section.id}-toggle`}
              className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-gray-50"
            >
              <span className="font-medium text-gray-900">{section.title || 'Section'}</span>
              <span className="text-gray-400">{isOpen ? '−' : '+'}</span>
            </button>
            {isOpen && (
              <dl className="space-y-2 px-4 pb-4">
                {section.fields.map((field) => (
                  <div
                    key={field.id}
                    className="grid grid-cols-3 gap-2 text-sm"
                    data-testid={`registration-snapshot-row-${field.key}`}
                  >
                    <dt className="text-gray-500">{field.label}</dt>
                    <dd className="col-span-2 text-gray-900">
                      {renderValue(
                        field.type,
                        field.key ? response.responses[field.key] : undefined
                      )}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        );
      })}
    </div>
  );
}

function renderValue(type: string, value: unknown): React.ReactNode {
  if (value === undefined || value === null || value === '') {
    return <span className="italic text-gray-400">(not provided)</span>;
  }
  if (type === FormFieldType.FILE) {
    const f = value as { fileName?: string };
    if (f.fileName) return <span>{f.fileName}</span>;
  }
  if (type === FormFieldType.SIGNATURE) {
    return <span className="italic">{String(value)}</span>;
  }
  if (type === FormFieldType.CONTACT_LIST && Array.isArray(value)) {
    return (
      <ul className="list-disc pl-5">
        {value.map((c, i) => {
          const e = c as { name?: string; relationship?: string; phone?: string };
          return (
            <li key={i}>
              {e.name} ({e.relationship}){e.phone ? ` — ${e.phone}` : ''}
            </li>
          );
        })}
      </ul>
    );
  }
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}
